import { useApolloClient } from '@apollo/client';
import { groupBy, isEmpty, isNil } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Queries } from '@shared/api';
import { KnownCard, LastKnownCards } from '@shared/data/cards/matching/LastKnownCards';
import { getAllCardMatch } from '@shared/data/cards/matching/cardMatch';
import { getCardMatchLookups } from '@shared/data/cards/matching/cardMatchLookups';
import { getCardComparator, hasImmutableSort } from '@shared/data/cards/matching/getCardComparator';
import { getCardFilterMatch } from '@shared/data/cards/matching/getCardFilterMatch';
import { getCardSearchMatch } from '@shared/data/cards/matching/getCardSearchMatch';
import { getListUpdate } from '@shared/data/cards/matching/getListUpdate';
import {
  LastKnownCardsContext,
  useCardUpdatesActions,
} from '@shared/scenes/cardUpdates/CardUpdatesContext';
import { useDraggableContext } from '@shared/scenes/cards/listView/components/DraggableContext';
import { API } from '@shared/types/api';
import { useCurrentUserId } from '@shared/util/hooks';
import {
  LocalWorkflowEntityChange,
  useLocalWorkflowEntityChange,
} from './LocalWorkflowEntityChanges';
import { useWorkflowEntityEvent } from './WorkflowEntityEvents';
import { useStaggeredRefresh } from './useStaggeredRefresh';

export const CARDS_PER_PAGE = 50;
export const CARDS_PER_GROUP_PAGE = 10;

const LIST_EVENT_TYPES: API.EntityEventType[] = [
  'WORKFLOW_ENTITY_CREATED',
  'WORKFLOW_ENTITY_RESTORED',
  'WORKFLOW_ENTITY_UPDATED',
  'WORKFLOW_ENTITY_DELETED',
];

type EntityRefs = API.ListWorkflowEntitiesQuery['list']['refs'];

interface ListChange {
  type: API.EntityEventType;
  entities: API.WorkflowEntity[];
  refs?: EntityRefs | null;
  // what the change is recorded against in the last known cards, once however many lists apply it.
  // Compared by reference only, so it has to be the object every list is handed, never a copy.
  event: API.WorkflowEntitySubscription | LocalWorkflowEntityChange;
  // set only for a change of this client's own: the cards as it had them before the change
  previousEntities?: API.WorkflowEntity[];
}

export interface UpdateListOptions {
  // the card query the list belongs to, which an event the list can't settle marks as stale
  queryId: string;
  // the list as rendered, which seeds the last known cards with each card the list loads
  renderedList?: API.ListWorkflowEntitiesQuery;
  // whether the list's query is fetching, so the list it renders once done is the server's own
  isLoading?: boolean;
}

export interface UpdatedList {
  // records the cards of a list just fetched from the server, such as by a refetch
  replaceKnownCards: (list: API.ListWorkflowEntitiesQuery | undefined) => void;
}

/**
 * Keeps one card list's cached page and count in step with workflow entity events, and with the
 * changes this client makes as soon as their mutations return, without asking the server. Each
 * card is matched against the filters, search and sort the list's query sent. An event that may
 * have changed which cards the list shows, in a way it can't settle, leaves it as it was and offers
 * the customer a refetch of it; one that leaves only the count in doubt has the count refreshed on
 * its own.
 */
export function useUpdateList(
  variables: API.ListWorkflowEntitiesQueryVariables,
  { queryId, renderedList, isLoading = false }: UpdateListOptions,
): UpdatedList {
  const client = useApolloClient();
  const currentUserId = useCurrentUserId();
  const lastKnownCards = useLastKnownCards(renderedList, isLoading);
  const { request: refreshCount, noteChange: noteListChange } = useCountRefresh(variables);
  const { markStale, clearStale, isStale } = useCardUpdatesActions();

  // new variables mean a fresh fetch, which replaces whatever the list was unsure of
  useEffect(() => {
    clearStale(queryId);
  }, [clearStale, queryId, variables]);

  // The count can move outside events too - a drag writes it in the cache - and the search index
  // takes a moment to catch up with any move, so every one counts as a change.
  const renderedCount = renderedList?.list.totalResults;
  useEffect(() => {
    if (!isNil(renderedCount)) {
      noteListChange();
    }
  }, [noteListChange, renderedCount]);

  // This client's own changes are recorded against the list that applied them, so the event
  // reporting each one finds it applied and moves nothing again. Kept per list: a list that left an
  // own change to its event has to count that event in full. Keyed by the variables rather than
  // the query id, which a list that remounts doesn't keep.
  const listKey = useMemo(() => JSON.stringify(variables), [variables]);

  const applyChange = ({ type, entities, refs, event, previousEntities }: ListChange) => {
    const { workflowTemplateId, filterByComponents, omniSearchQuery, sortByComponents } =
      variables.input;

    const isOwnChange = !isNil(previousEntities);
    const cards = (groupBy(entities, 'workflowTemplateId')[workflowTemplateId] ?? []).filter(
      // an own change that an event already reported is applied already
      card =>
        !isOwnChange || !isAlreadyReported(type, card, lastKnownCards.getBefore(card.id, event)),
    );

    // change is not for this workflow template, nothing to update
    if (isEmpty(cards) || !LIST_EVENT_TYPES.includes(type)) {
      return;
    }

    const existingCards = client.readQuery<
      API.ListWorkflowEntitiesQuery,
      API.ListWorkflowEntitiesQueryVariables
    >({
      query: Queries.workflow.listWorkflowEntities,
      variables,
    });

    // this query is not in the cache, nothing to update
    if (isNil(existingCards)) {
      return;
    }

    // An own change carries the related cards only when its mutation returned them. Without them
    // a cross-workflow filter can't be settled, and the change is left to its event, which has them.
    const lookups = getCardMatchLookups({
      eventRefs: refs,
      listRefs: existingCards.list.refs,
      currentUserId,
    });

    const getPreviousCard = (cardId: string): KnownCard | undefined => {
      const known = lastKnownCards.getBefore(cardId, event);
      if (isOwnChange) {
        // the cache's copy from before the mutation - unless an event has reported a newer one
        // since, such as another client's edit landing while the mutation was in flight
        const ownPrevious = previousEntities.find(card => card.id === cardId);
        if (isNil(ownPrevious) || (!isNil(known) && isSameOrNewer(known.card, ownPrevious))) {
          return known;
        }
        return { card: ownPrevious, isReported: false };
      }
      // the event for an own change this list applied finds the card where the change left it
      const card = cards.find(eventCard => eventCard.id === cardId);
      if (!isNil(card) && lastKnownCards.isOwnChangeApplied(listKey, type, card)) {
        return { card, isReported: true };
      }
      return known;
    };

    const update = getListUpdate({
      type,
      cards,
      list: existingCards.list,
      isStale: isStale(queryId),
      getMatch: (card, listedCard) =>
        getAllCardMatch([
          getCardFilterMatch(card, filterByComponents, lookups, listedCard),
          getCardSearchMatch(card, omniSearchQuery, lookups),
        ]),
      getPreviousCard,
      compareCards: getCardComparator(sortByComponents, workflowTemplateId, lookups),
      hasImmutableSort: hasImmutableSort(sortByComponents),
      skipUnsettled: isOwnChange,
    });

    if (update.isStale) {
      markStale(queryId);
    } else if (update.isCountStale) {
      refreshCount();
    }

    client.writeQuery({
      query: Queries.workflow.listWorkflowEntities,
      variables,
      data: {
        ...existingCards,
        list: {
          ...existingCards.list,
          entities: update.entities,
          totalResults: update.totalResults,
          refs: isNil(refs)
            ? existingCards.list.refs
            : mergeEntityRefs(existingCards.list.refs, refs),
        },
      },
    });
    // an event that left the count alone, settled, leaves nothing for the search index to catch up on
    if (update.isStale || update.isCountStale) {
      noteListChange();
    }

    if (isOwnChange) {
      update.settledCardIds.forEach(cardId => {
        const card = cards.find(ownCard => ownCard.id === cardId);
        if (!isNil(card)) {
          lastKnownCards.recordOwnChange(listKey, type, card);
        }
      });
    } else {
      // only what the server reported is recorded, so every list reads the same earlier version
      cards.forEach(card => {
        lastKnownCards.record(card, event);
        lastKnownCards.clearOwnChange(listKey, card);
      });
    }
  };

  useWorkflowEntityEvent(subscriptionData =>
    applyChange({ ...subscriptionData.event, event: subscriptionData }),
  );
  useLocalWorkflowEntityChange(change => applyChange({ ...change, event: change }));

  const replaceKnownCards = useCallback(
    (list: API.ListWorkflowEntitiesQuery | undefined) => {
      list?.list.entities?.forEach(card => {
        if (!isNil(card)) {
          lastKnownCards.replace(card);
        }
      });
    },
    [lastKnownCards],
  );

  return { replaceKnownCards };
}

export interface CountRefresh {
  // asks for the count to be refreshed, once the search index can be trusted to hold it
  request: VoidFunction;
  // records that the list's count just moved, or that a change couldn't be counted
  noteChange: VoidFunction;
}

/**
 * Refreshes a list's count with a count query, which skips the sorting, cards and refs a list query
 * fetches. Staggered like any refresh a remote change calls for, and held while a card is dragged,
 * as a drag moves counts itself.
 *
 * The count it brings back replaces the one the list has been moving, so it has to include every
 * change the list has applied. It waits until the search index has had time for the latest one,
 * and asks again when the count moves while its query is out - by an event, or by a drag writing
 * the count itself - unless the list has moved on to other variables, whose fetch brings a count.
 */
export function useCountRefresh(variables: API.ListWorkflowEntitiesQueryVariables): CountRefresh {
  const client = useApolloClient();
  const { isDragging } = useDraggableContext();
  const lastChangeAtRef = useRef<number | null>(null);
  const changeCountRef = useRef(0);
  const variablesRef = useRef(variables);
  const requestRef = useRef<VoidFunction>(() => {});

  const refreshCount = useCallback(
    (requestedVariables: API.ListWorkflowEntitiesQueryVariables) => {
      const { workflowTemplateId, viewTemplateId, filterByComponents, omniSearchQuery } =
        requestedVariables.input;
      const readCount = () =>
        client.readQuery<API.ListWorkflowEntitiesQuery, API.ListWorkflowEntitiesQueryVariables>({
          query: Queries.workflow.listWorkflowEntities,
          variables: requestedVariables,
        })?.list.totalResults;
      const changeCount = changeCountRef.current;
      const sentCount = readCount();
      client
        .query<API.CountWorkflowEntitiesQuery, API.CountWorkflowEntitiesQueryVariables>({
          query: Queries.workflow.countWorkflowEntities,
          variables: {
            input: { workflowTemplateId, viewTemplateId, filterByComponents, omniSearchQuery },
          },
          fetchPolicy: 'no-cache',
        })
        .then(({ data }) => {
          const totalResults = data?.countWorkflowEntities?.totalResults;
          if (isNil(totalResults)) {
            return;
          }
          // the count may predate a change applied while the query was out
          if (changeCountRef.current !== changeCount || readCount() !== sentCount) {
            if (variablesRef.current === requestedVariables) {
              requestRef.current();
            }
            return;
          }
          client.cache.updateQuery<
            API.ListWorkflowEntitiesQuery,
            API.ListWorkflowEntitiesQueryVariables
          >(
            { query: Queries.workflow.listWorkflowEntities, variables: requestedVariables },
            existing =>
              isNil(existing)
                ? existing
                : { ...existing, list: { ...existing.list, totalResults } },
          );
        })
        // the count stays where the events left it
        .catch(() => {});
    },
    [client],
  );

  const request = useStaggeredRefresh(refreshCount, {
    key: variables,
    isHeld: isDragging,
    getLastChangeAt: () => lastChangeAtRef.current,
  });
  useEffect(() => {
    variablesRef.current = variables;
    requestRef.current = request;
  });

  const noteChange = useCallback(() => {
    lastChangeAtRef.current = Date.now();
    changeCountRef.current += 1;
  }, []);

  return useMemo(() => ({ request, noteChange }), [request, noteChange]);
}

/**
 * Whether an event already reported the change this client made to a card - it can arrive before
 * the mutation that made the change returns. A deletion or restoration is told by `deletedAt`, as
 * it leaves `updatedAt` as it was.
 */
function isAlreadyReported(
  type: API.EntityEventType,
  card: API.WorkflowEntity,
  known: KnownCard | undefined,
): boolean {
  if (isNil(known) || !known.isReported) {
    return false;
  }
  const isKnownDeleted = !isNil(known.card.fields?.deletedAt);
  switch (type) {
    case 'WORKFLOW_ENTITY_DELETED':
      return isKnownDeleted;
    case 'WORKFLOW_ENTITY_RESTORED':
      return !isKnownDeleted;
    case 'WORKFLOW_ENTITY_CREATED':
      return true;
    default:
      return isSameOrNewer(known.card, card);
  }
}

function isSameOrNewer(card: API.WorkflowEntity, other: API.WorkflowEntity): boolean {
  return new Date(card.updatedAt).getTime() >= new Date(other.updatedAt).getTime();
}

/**
 * The collection's shared last known cards, or the list's own outside a collection view. Each
 * card the list loads is recorded in it.
 */
function useLastKnownCards(
  renderedList: API.ListWorkflowEntitiesQuery | undefined,
  isLoading: boolean,
): LastKnownCards {
  const sharedLastKnownCards = useContext(LastKnownCardsContext);
  const [ownLastKnownCards] = useState(() => new LastKnownCards());
  const lastKnownCards = sharedLastKnownCards ?? ownLastKnownCards;
  const wasLoadingRef = useRef(isLoading);

  useEffect(() => {
    // the list rendered as its fetch finished holds the server's own versions of its cards
    const isFetched = wasLoadingRef.current && !isLoading;
    wasLoadingRef.current = isLoading;
    renderedList?.list.entities?.forEach(card => {
      if (isNil(card)) {
        return;
      }
      if (isFetched) {
        lastKnownCards.replace(card);
      } else {
        lastKnownCards.load(card);
      }
    });
  }, [lastKnownCards, renderedList, isLoading]);

  return lastKnownCards;
}

function mergeEntityRefs(existingRefs: EntityRefs, incomingRefs: EntityRefs): EntityRefs {
  return {
    ...existingRefs,
    workflowEntityRefs: mergeRefs(
      existingRefs?.workflowEntityRefs,
      incomingRefs?.workflowEntityRefs,
    ),
    personRefs: mergeRefs(existingRefs?.personRefs, incomingRefs?.personRefs),
    recurringEntitiesScheduleRefs: mergeRefs(
      existingRefs?.recurringEntitiesScheduleRefs,
      incomingRefs?.recurringEntitiesScheduleRefs,
    ),
    workflowTemplateRefs: mergeRefs(
      existingRefs?.workflowTemplateRefs,
      incomingRefs?.workflowTemplateRefs,
    ),
  };
}

function mergeRefs<RefItem extends { id: string | number }>(
  existingRefList: RefItem[] | null | undefined,
  incomingRefList: RefItem[] | null | undefined,
): RefItem[] | null {
  if (isNil(existingRefList) || isNil(incomingRefList)) {
    // if either is null, return the other one, or null
    return existingRefList ?? incomingRefList ?? null;
  }

  // merge refs, using incomingRefList as the source of truth, and re-add any
  // existing refs that are not in the incomingRefList
  const mergedRefList = existingRefList.reduce((memo, existingRef) => {
    if (memo.some(incomingRef => incomingRef.id === existingRef.id)) {
      return memo;
    } else {
      return [...memo, existingRef];
    }
  }, incomingRefList);

  return mergedRefList;
}
